/* eslint-disable @typescript-eslint/no-explicit-any */
import { Row, Segmented, notification, Spin } from "antd";
import Icon from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Styles from "./op.module.scss";
import LocationStyles from "./toteInfo.module.scss";
import UniversalScanButton from "../../components/mobile/UniversalScanButton";
import LocationConfirmationModal from "../../components/mobile/LocationConfirmationModal";
import TrolleyLocationConfirmationModal from "../../components/mobile/TrolleyLocationConfirmationModal";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import { api } from "../../services/api";
import ListingCards from "../../components/reusable/listingCards";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAllTotes, useAllTrolleys, useLocations } from "../common/queries";
import type { Trolley, Tote } from "../../services/types";
import { LeftIcon } from "../../assets/icons/icons";
import CONSTANTS from "../../constants/constants";
import axios from "axios";
import {
  isFALPoULocation,
  isAirlogEmptyHoldingLocation,
  hasTotesLoaded,
  isTrolleyEmpty,
} from "../../utils/locationUtils";

interface ListingItem {
  toteNo: string | number;
  status: string;
  totalItems: number;
  priority: boolean;
  id?: string;
  fullData?: unknown;
  registeredDate?: string;
}

const ITEMS_PER_PAGE = 10;
const SCROLL_THRESHOLD = 300;

const LocationInfo = () => {
  const { locationId: encodedLocationId } = useParams<{ locationId: string }>();
  const locationId = encodedLocationId
    ? decodeURIComponent(encodedLocationId)
    : undefined;
  const [searchParams] = useSearchParams();
  const { data: locationsData } = useLocations();

  const matchedLocation = useMemo(() => {
    if (!locationId || !locationsData?.locations?.length) {
      return undefined;
    }

    const normalizedId = locationId.toLowerCase();
    return (
      locationsData.locations.find(
        (loc) => loc.location_barcode.toLowerCase() === normalizedId
      ) ||
      locationsData.locations.find(
        (loc) => loc.primary_location.toLowerCase() === normalizedId
      )
    );
  }, [locationId, locationsData?.locations]);

  const locationBarcode = matchedLocation?.location_barcode || locationId;

  // Parse location_id - assume it's a number or can be converted to number
  // If parsing fails, locationIdNumber will be undefined, and we'll filter client-side by location name
  const locationIdNumber =
    matchedLocation?.primary_location_id ??
    (locationId && !isNaN(parseInt(locationId, 10))
      ? parseInt(locationId, 10)
      : undefined);

  // state
  const [segment, setSegment] = useState("Tote");
  const [displayedItems, setDisplayedItems] = useState<ListingItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isCheckingTote, setIsCheckingTote] = useState(false);
  const [isCheckingTrolley, setIsCheckingTrolley] = useState(false);
  const [locationName, setLocationName] = useState<string>("");
  const [toteLocationSuccessOpen, setToteLocationSuccessOpen] = useState(false);
  const [toteLocationSuccessMessage, setToteLocationSuccessMessage] =
    useState<string>("");
  const [toteLocationSuccessToteId, setToteLocationSuccessToteId] = useState<
    string | null
  >(null);
  const [isToteLocationModalOpen, setIsToteLocationModalOpen] = useState(false);
  const [isTrolleyLocationModalOpen, setIsTrolleyLocationModalOpen] =
    useState(false);
  const [pendingToteCode, setPendingToteCode] = useState<string | null>(null);
  const [pendingTrolleyCode, setPendingTrolleyCode] = useState<string | null>(
    null
  );
  const [selectedToteData, setSelectedToteData] = useState<Tote | null>(null);
  const [selectedTrolleyData, setSelectedTrolleyData] =
    useState<Trolley | null>(null);
  const [isUpdatingToteLocation, setIsUpdatingToteLocation] = useState(false);
  const [isUpdatingTrolleyLocation, setIsUpdatingTrolleyLocation] =
    useState(false);
  const [locationSuccessOpen, setLocationSuccessOpen] = useState(false);
  const [locationSuccessMessage, setLocationSuccessMessage] = useState<any>("");
  const [trolleyLocationSuccessId, setTrolleyLocationSuccessId] = useState<
    string | null
  >(null);
  const [scanErrorMessage, setScanErrorMessage] = useState<string | null>(null);
  const scanErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [toteSecondaryLocationActions, setToteSecondaryLocationActions] =
    useState<Map<string, "In" | "Out">>(new Map());
  const [trolleySecondaryLocationActions, setTrolleySecondaryLocationActions] =
    useState<Map<string, "In" | "Out">>(new Map());

  // React Query hooks - Try to filter by location_id if it's a number
  const {
    data: allTotes = [],
    isLoading: totesLoading,
    error: totesError,
    refetch: refetchTotes,
  } = useAllTotes({
    page: 1,
    page_size: 100,
    ...(locationIdNumber ? { location_id: locationIdNumber } : {}),
    ...(locationBarcode ? { location_barcode: locationBarcode } : {}),
  });
  const {
    data: allTrolleys = [],
    isLoading: trolleysLoading,
    error: trolleysError,
    refetch: refetchTrolleys,
  } = useAllTrolleys({
    page: 1,
    page_size: 100,
    ...(locationIdNumber ? { location_id: locationIdNumber } : {}),
    ...(locationBarcode ? { location_barcode: locationBarcode } : {}),
  });

  // Filter by location name if locationId is not a number (client-side filtering)
  // The API might return all items, so we filter by matching the location field
  const totes = useMemo(() => {
    if (!locationId) return [];
    if (locationIdNumber || locationBarcode) {
      return allTotes.filter((tote) => {
        if (locationBarcode && tote.locationBarcode) {
          return tote.locationBarcode === locationBarcode;
        }

        if (locationIdNumber && typeof tote.locationBarcode === "string") {
          return tote.locationBarcode === locationIdNumber.toString();
        }

        if (!locationBarcode) {
          return true;
        }

        const toteLocation = tote.lastScannedLocation || "";
        if (!toteLocation) return false;

        const normalizedLocationId = locationBarcode.toLowerCase().trim();
        const normalizedLocation = toteLocation.toLowerCase();
        const locationParts = normalizedLocation.split(" • ");
        return (
          locationParts[0] === normalizedLocationId ||
          normalizedLocation.includes(normalizedLocationId) ||
          normalizedLocationId === normalizedLocation
        );
      });
    }

    return allTotes;
  }, [allTotes, locationId, locationIdNumber, locationBarcode]);

  const trolleys = useMemo(() => {
    if (!locationId) return [];
    if (locationIdNumber || locationBarcode) {
      return allTrolleys.filter((trolley) => {
        if (locationBarcode && trolley.locationBarcode) {
          return trolley.locationBarcode === locationBarcode;
        }

        if (locationIdNumber && typeof trolley.locationBarcode === "string") {
          return trolley.locationBarcode === locationIdNumber.toString();
        }

        if (!locationBarcode) {
          return true;
        }

        const trolleyLocation = trolley.lastScannedLocation || "";
        if (!trolleyLocation) return false;

        const normalizedLocationId = locationBarcode.toLowerCase().trim();
        const normalizedLocation = trolleyLocation.toLowerCase();
        const locationParts = normalizedLocation.split(" • ");
        return (
          locationParts[0] === normalizedLocationId ||
          normalizedLocation.includes(normalizedLocationId) ||
          normalizedLocationId === normalizedLocation
        );
      });
    }

    return allTrolleys;
  }, [allTrolleys, locationId, locationIdNumber, locationBarcode]);

  // Determine current data based on segment
  const isLoading = segment === "Tote" ? totesLoading : trolleysLoading;
  const error = segment === "Tote" ? totesError : trolleysError;

  // Set location name from locationId
  useEffect(() => {
    if (matchedLocation) {
      setLocationName(
        `${matchedLocation.primary_location} (${matchedLocation.location_barcode})`
      );
    } else if (locationId) {
      setLocationName(`Location: ${locationId}`);
    }
  }, [matchedLocation, locationId]);

  useEffect(() => {
    setIsToteLocationModalOpen(false);
    setIsTrolleyLocationModalOpen(false);
    setPendingToteCode(null);
    setPendingTrolleyCode(null);
    setSelectedToteData(null);
  }, [locationBarcode]);

  // Calculate available totes for each trolley (use API value if available, otherwise calculate)
  const getAvailableTotesCount = useCallback(
    (trolley: Trolley): number => {
      // Use API value if available
      if (trolley.availableTotes !== undefined) {
        return trolley.availableTotes;
      }
      // Fallback: calculate from totes list
      return totes.filter((tote) => tote.trolleyId === trolley.id).length;
    },
    [totes]
  );

  // Transform data for listing view
  const transformData = useCallback((): ListingItem[] => {
    if (segment === "Tote") {
      return totes.map((tote) => ({
        toteNo: tote.toteNo,
        status: tote.activityStatus,
        totalItems: parseInt(tote.itemCount) || 0,
        priority: tote.priority,
        id: tote.id,
        fullData: tote,
        registeredDate: tote.registeredDate,
      }));
    } else {
      return trolleys.map((trolley) => ({
        toteNo: trolley.id,
        status: trolley.status,
        totalItems: getAvailableTotesCount(trolley),
        priority: trolley.priority,
        id: trolley.id,
        fullData: trolley,
        registeredDate: trolley.registeredDate,
      }));
    }
  }, [segment, totes, trolleys, getAvailableTotesCount]);

  // Apply filters and search (API handles filtering for both totes and trolleys now)
  const applyFiltersAndSearch = useCallback(
    (data: ListingItem[]): ListingItem[] => {
      // For both totes and trolleys, API handles filtering, so just return data as-is
      // Client-side filtering is no longer needed
      return data;
    },
    []
  );

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    // Simulate API delay
    setTimeout(() => {
      const allData = transformData();
      const filteredData = applyFiltersAndSearch(allData);

      const nextPageStart = displayedItems.length;
      const nextPageEnd = nextPageStart + ITEMS_PER_PAGE;
      const nextItems = filteredData.slice(nextPageStart, nextPageEnd);

      if (nextItems.length > 0) {
        setDisplayedItems((prev) => [...prev, ...nextItems]);
        setHasMore(nextPageEnd < filteredData.length);
      } else {
        setHasMore(false);
      }

      setIsLoadingMore(false);
    }, 500);
  }, [
    displayedItems.length,
    isLoadingMore,
    hasMore,
    transformData,
    applyFiltersAndSearch,
  ]);

  // Reset and load initial items
  useEffect(() => {
    const allData = transformData();
    const filteredData = applyFiltersAndSearch(allData);

    setDisplayedItems(filteredData.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredData.length > ITEMS_PER_PAGE);
  }, [segment, totes, trolleys, transformData, applyFiltersAndSearch]);

  // Handle scroll for infinite scroll
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD && hasMore && !isLoadingMore) {
      loadMoreItems();
    }
  }, [hasMore, isLoadingMore, loadMoreItems]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  // Error handling
  useEffect(() => {
    if (error) {
      notification.error({
        message: "Failed to fetch the list! Try again later",
        description:
          segment === "Tote"
            ? "Failed to load tote list. Please try again later."
            : "Failed to load trolley list. Please try again later.",
        duration: 5,
      });
    }
  }, [error, segment]);

  // Handle resume from tote registration
  useEffect(() => {
    const PENDING_LOCATION_TOTE_KEY = "pendingLocationToteContext";

    try {
      const stored = sessionStorage.getItem(PENDING_LOCATION_TOTE_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored) as {
        toteId: string;
        locationId?: string;
        locationBarcode?: string;
        openModal?: boolean;
        segment?: string;
      } | null;

      if (parsed && parsed.toteId) {
        const contextLocationId = parsed.locationBarcode || parsed.locationId;

        // Check if we're on the correct location page
        if (contextLocationId && contextLocationId === locationId) {
          // Set segment to Tote if specified
          if (parsed.segment === "Tote") {
            setSegment("Tote");
          }

          // If openModal flag is set, automatically open the location confirmation modal
          if (parsed.openModal) {
            void (async () => {
              try {
                setIsCheckingTote(true);
                const toteInfo = await api.getToteById(parsed.toteId);
                setSelectedToteData(toteInfo);
                setPendingToteCode(parsed.toteId);
                setIsToteLocationModalOpen(true);
              } catch (error) {
                console.warn("Failed to resume location tote context", error);
                notification.error({
                  message: "Unable to resume",
                  description: "Please rescan the tote to continue.",
                });
              } finally {
                setIsCheckingTote(false);
                sessionStorage.removeItem(PENDING_LOCATION_TOTE_KEY);
              }
            })();
          } else {
            // Clear the context if modal flag is not set
            sessionStorage.removeItem(PENDING_LOCATION_TOTE_KEY);
          }
        }
      }
    } catch (error) {
      console.warn("Failed to parse pending location tote context", error);
      sessionStorage.removeItem(PENDING_LOCATION_TOTE_KEY);
    }
  }, [locationId]);

  // Handle resume from trolley registration
  useEffect(() => {
    const PENDING_LOCATION_TROLLEY_KEY = "pendingLocationTrolleyContext";

    try {
      const stored = sessionStorage.getItem(PENDING_LOCATION_TROLLEY_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored) as {
        trolleyId: string;
        locationId?: string;
        locationBarcode?: string;
        openModal?: boolean;
        segment?: string;
      } | null;

      if (parsed && parsed.trolleyId) {
        const contextLocationId = parsed.locationBarcode || parsed.locationId;

        // Check if we're on the correct location page
        if (contextLocationId && contextLocationId === locationId) {
          // Set segment to Trolley if specified
          if (parsed.segment === "Trolley") {
            setSegment("Trolley");
          }

          // If openModal flag is set, automatically open the location confirmation modal
          if (parsed.openModal) {
            void (async () => {
              try {
                setIsCheckingTrolley(true);
                const trolleyInfo = await api.getTrolleyById(parsed.trolleyId);
                setSelectedTrolleyData(trolleyInfo);
                setPendingTrolleyCode(parsed.trolleyId);
                setIsTrolleyLocationModalOpen(true);
              } catch (error) {
                console.warn("Failed to resume location trolley context", error);
                notification.error({
                  message: "Unable to resume",
                  description: "Please rescan the trolley to continue.",
                });
              } finally {
                setIsCheckingTrolley(false);
                sessionStorage.removeItem(PENDING_LOCATION_TROLLEY_KEY);
              }
            })();
          } else {
            // Clear the context if modal flag is not set
            sessionStorage.removeItem(PENDING_LOCATION_TROLLEY_KEY);
          }
        }
      }
    } catch (error) {
      console.warn("Failed to parse pending location trolley context", error);
      sessionStorage.removeItem(PENDING_LOCATION_TROLLEY_KEY);
    }
  }, [locationId]);

  // No need for confirmation handling - warning page handles the API call directly

  const handleToteScanned = async (code: string): Promise<boolean> => {
    console.log("Tote scanned for adding to location:", code);

    // Clear any existing error message
    if (scanErrorTimerRef.current) {
      clearTimeout(scanErrorTimerRef.current);
      scanErrorTimerRef.current = null;
    }
    setScanErrorMessage(null);

    setIsCheckingTote(true);
    setSelectedToteData(null);

    try {
      const response = await api.checkTote({
        tote_id: code,
      });

      // Check if tote is registered (has tote_details in response)
      if (
        response.status_code === 200 &&
        (response.tote_details?.tote_id || response.tote_details?.id)
      ) {
        // Extract the record ID from checkTote response
        const toteRecordId = response.tote_details?.id;
        // Use record ID to fetch tote details, fallback to scanned code if record ID not available
        const toteIdToFetch = toteRecordId ? String(toteRecordId) : code;

        // Fetch tote details to check status
        try {
          let toteDetails = await api.getToteById(toteIdToFetch);

          // Ensure recordId is set from checkTote response if not already set
          if (toteRecordId && !toteDetails.recordId) {
            toteDetails = { ...toteDetails, recordId: toteRecordId };
          }
          setSelectedToteData(toteDetails);

          // Validate tote status
          const status = toteDetails.activityStatus?.toLowerCase() || "";

          if (status === "loaded to trolley") {
            setScanErrorMessage(
              "Tote location cannot be updated while it is loaded on a trolley."
            );
            scanErrorTimerRef.current = setTimeout(() => {
              setScanErrorMessage(null);
              scanErrorTimerRef.current = null;
            }, 5000);
            return true; // Keep scanner open
          }

          if (status === "unloaded from trolley") {
            setScanErrorMessage("Tote already unloaded — cannot move.");
            scanErrorTimerRef.current = setTimeout(() => {
              setScanErrorMessage(null);
              scanErrorTimerRef.current = null;
            }, 5000);
            return true; // Keep scanner open
          }

          // If status is "Available", proceed with location confirmation modal
          if (status === "available") {
            setPendingToteCode(code);
            setIsToteLocationModalOpen(true);
            return true;
          }

          // For any other status, show generic error
          setScanErrorMessage("Tote cannot be moved in its current status.");
          scanErrorTimerRef.current = setTimeout(() => {
            setScanErrorMessage(null);
            scanErrorTimerRef.current = null;
          }, 5000);
          return true;
        } catch (detailsError) {
          console.warn("Failed to fetch tote details", detailsError);
          setScanErrorMessage(
            "Failed to verify tote details. Please try again."
          );
          scanErrorTimerRef.current = setTimeout(() => {
            setScanErrorMessage(null);
            scanErrorTimerRef.current = null;
          }, 5000);
          return true;
        }
      } else {
        // Tote not registered - redirect to register flow
        const PENDING_LOCATION_TOTE_KEY = "pendingLocationToteContext";
        sessionStorage.setItem(
          PENDING_LOCATION_TOTE_KEY,
          JSON.stringify({
            toteId: code,
            locationId,
            locationBarcode,
          })
        );

        setTimeout(() => {
          navigate(`/register-tote-error?toteId=${encodeURIComponent(code)}`);
        }, 500);

        return true;
      }
    } catch (error: any) {
      console.log("Tote check failed:", error);
      // Tote not registered - redirect to register flow
      const PENDING_LOCATION_TOTE_KEY = "pendingLocationToteContext";
      sessionStorage.setItem(
        PENDING_LOCATION_TOTE_KEY,
        JSON.stringify({
          toteId: code,
          locationId,
          locationBarcode,
        })
      );

      setTimeout(() => {
        navigate(`/register-tote-error?toteId=${encodeURIComponent(code)}`);
      }, 500);

      return true;
    } finally {
      setIsCheckingTote(false);
    }
  };

  const handleTrolleyScanned = async (code: string) => {
    console.log("Trolley scanned for loading to location:", code);
    setIsCheckingTrolley(true);
    setSelectedTrolleyData(null);

    try {
      // Use checkTrolley API to verify trolley exists
      const response = await api.checkTrolley({
        trolley_id: code,
      });

      const hasTrolleyDetails = Boolean(
        response?.trolley_details?.trolley_id || response?.trolley_details?.id
      );

      // Check message to determine if trolley is registered
      if (
        (response.message && response.message.includes("isn't registered")) ||
        !hasTrolleyDetails
      ) {
        // Store pending location trolley context before navigating to registration
        const PENDING_LOCATION_TROLLEY_KEY = "pendingLocationTrolleyContext";
        sessionStorage.setItem(
          PENDING_LOCATION_TROLLEY_KEY,
          JSON.stringify({
            trolleyId: code,
            locationId,
            locationBarcode,
          })
        );

        notification.info({
          message: "Trolley Not Found",
          description:
            response?.message ||
            `Trolley ID: ${code} is not registered in our system`,
        });
        // Navigate to registration page instead of landing page
        setTimeout(() => {
          navigate(`/register-trolley-error?trolleyId=${encodeURIComponent(code)}`);
        }, 1000);
        return;
      } else {
        // Fetch full trolley details to get status
        try {
          const trolleyData = await api.getTrolleyById(code);
          setSelectedTrolleyData(trolleyData);
          setPendingTrolleyCode(code);
          setIsTrolleyLocationModalOpen(true);
        } catch (error) {
          console.error("Failed to fetch trolley details", error);
          setScanErrorMessage(
            "Failed to fetch trolley details. Please try again."
          );
          setTimeout(() => setScanErrorMessage(null), 5000);
        }
      }
    } catch (error: any) {
      // API error - trolley not found
      console.log("Trolley check failed:", error);
      
      // Store pending location trolley context before navigating to registration
      const PENDING_LOCATION_TROLLEY_KEY = "pendingLocationTrolleyContext";
      sessionStorage.setItem(
        PENDING_LOCATION_TROLLEY_KEY,
        JSON.stringify({
          trolleyId: code,
          locationId,
          locationBarcode,
        })
      );

      notification.info({
        message: "Trolley Not Found",
        description: `Trolley ID: ${code} is not registered in our system`,
      });
      // Navigate to registration page instead of landing page
      setTimeout(() => {
        navigate(`/register-trolley-error?trolleyId=${encodeURIComponent(code)}`);
      }, 1000);
    } finally {
      setIsCheckingTrolley(false);
    }
  };

  const handleLocationScanned = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      return;
    }

    // Check if location exists in the locations list
    const normalizedCode = trimmed.toLowerCase();
    const matchedLocation =
      locationsData?.locations?.find(
        (loc) => loc.location_barcode?.toLowerCase() === normalizedCode
      ) ||
      locationsData?.locations?.find(
        (loc) => loc.primary_location?.toLowerCase() === normalizedCode
      );

    if (!matchedLocation) {
      // Location not found - show info message and navigate back to landing page
      notification.info({
        message: "Location Not Found",
        description: `Scanned location "${trimmed}" is not available in the system`,
      });
      setTimeout(() => {
        navigate(CONSTANTS.PAGE_ROUTES.OP_LANDING);
      }, 1000);
      return;
    }

    // Location found - navigate to location info page for the new location
    navigate(`/location-info/${encodeURIComponent(trimmed)}`);
  };

  const handleToteLocationCancel = () => {
    setIsToteLocationModalOpen(false);
    setPendingToteCode(null);
    setSelectedToteData(null);
  };


  const handleTrolleyLocationCancel = () => {
    setIsTrolleyLocationModalOpen(false);
    setPendingTrolleyCode(null);
    setSelectedTrolleyData(null);
  };

  const handleToteLocationConfirm = async (
    locationType: "In" | "Out" = "In"
  ) => {
    if (!pendingToteCode) {
      notification.error({
        message: "No Tote Selected",
        description: "Please rescan the tote and try again.",
      });
      return;
    }

    if (!locationBarcode) {
      notification.error({
        message: "Invalid Location",
        description: "Unable to determine the scanned location. Please rescan.",
      });
      return;
    }

    // Check if this is a secondary location and if action was already performed
    if (matchedLocation?.secondary_location) {
      const secondaryKey = `${matchedLocation.primary_location.toLowerCase()}|${matchedLocation.secondary_location.toLowerCase()}`;
      const performedAction = toteSecondaryLocationActions.get(secondaryKey);

      if (performedAction === locationType) {
        notification.error({
          message: "Action Already Performed",
          description: "Action already performed for this location.",
          duration: 3,
        });
        return;
      }
    }

    // Validation: Prevent tote from moving INTO or OUT of Airlog Empty Holding Area
    if (
      matchedLocation &&
      isAirlogEmptyHoldingLocation(matchedLocation.primary_location)
    ) {
      notification.error({
        message: "Action Not Allowed",
        description:
          "A Tote cannot be moved into or out of the Airlog Empty Holding Area.",
        duration: 5,
      });
      return;
    }

    try {
      setIsUpdatingToteLocation(true);
      const toteIdForSuccess = pendingToteCode;
      await api.updateToteLocation(
        selectedToteData?.recordId ?? pendingToteCode,
        locationBarcode,
        locationType
      );

      // Track the action for secondary locations
      if (matchedLocation?.secondary_location) {
        const secondaryKey = `${matchedLocation.primary_location.toLowerCase()}|${matchedLocation.secondary_location.toLowerCase()}`;
        setToteSecondaryLocationActions((prev) => {
          const newMap = new Map(prev);
          newMap.set(secondaryKey, locationType);
          return newMap;
        });
      }

      notification.success({
        message: "Tote Location Updated",
        description: `Tote ${pendingToteCode} updated to location (${locationType}).`,
      });
      handleToteLocationCancel();
      refetchTotes();
      setToteLocationSuccessToteId(toteIdForSuccess);
      setToteLocationSuccessMessage(
        `Tote ${toteIdForSuccess} ${
          locationType === "In"
            ? "is successfully assigned to"
            : "dispatched from"
        } ${
          matchedLocation?.primary_location ||
          locationName ||
          locationBarcode ||
          "Location"
        } (${locationType})${
          matchedLocation?.secondary_location
            ? ", " + matchedLocation.secondary_location
            : ""
        }`
      );
      setToteLocationSuccessOpen(true);
    } catch (error) {
      console.error("Failed to update tote location", error);
      const errorDescription = axios.isAxiosError(error)
        ? error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message
        : error instanceof Error
        ? error.message
        : "We couldn't update the tote location. Please try again.";

      // Check if error is related to invalid location
      const errorLower = errorDescription.toLowerCase();
      const isLocationError =
        errorLower.includes("location") &&
        (errorLower.includes("not found") ||
          errorLower.includes("invalid") ||
          errorLower.includes("not available") ||
          errorLower.includes("does not exist"));

      const locationDisplay =
        matchedLocation?.primary_location ||
        locationName ||
        locationBarcode ||
        "Location";

      if (isLocationError) {
        notification.info({
          message: "Location Not Found",
          description: `Scanned location "${locationDisplay}" is not available in the system`,
        });
      } else {
        notification.error({
          message: "Update Failed",
          description: errorDescription,
          duration: 5,
        });
      }

      // Close the modal on error and go back to location info page
      handleToteLocationCancel();
    } finally {
      setIsUpdatingToteLocation(false);
    }
  };

  const handleTrolleyLocationConfirm = async (
    locationType: "In" | "Out" = "In"
  ) => {
    if (!pendingTrolleyCode) {
      notification.error({
        message: "No Trolley Selected",
        description: "Please rescan the trolley and try again.",
      });
      return;
    }

    if (!locationBarcode) {
      notification.error({
        message: "Invalid Location",
        description: "Unable to determine the scanned location. Please rescan.",
      });
      return;
    }

    // Check if this is a secondary location and if action was already performed
    if (matchedLocation?.secondary_location) {
      const secondaryKey = `${matchedLocation.primary_location.toLowerCase()}|${matchedLocation.secondary_location.toLowerCase()}`;
      const performedAction = trolleySecondaryLocationActions.get(secondaryKey);

      if (performedAction === locationType) {
        notification.error({
          message: "Action Already Performed",
          description: "Action already performed for this location.",
          duration: 3,
        });
        return;
      }
    }

    // Validation for special locations
    if (matchedLocation && selectedTrolleyData) {
      const primaryLocation = matchedLocation.primary_location;
      const trolleyStatus = selectedTrolleyData.status;

      // Case 1: Moving OUT of FAL PoU with totes loaded
      if (
        locationType === "Out" &&
        isFALPoULocation(primaryLocation) &&
        hasTotesLoaded(trolleyStatus)
      ) {
        // Navigate to warning page
        const params = new URLSearchParams({
          trolleyId: pendingTrolleyCode,
          locationBarcode: locationBarcode,
          primaryLocation: matchedLocation.primary_location,
          secondaryLocation: matchedLocation.secondary_location || "",
          from: "location",
        });
        navigate(`/trolley-location-warning?${params.toString()}`);
        return;
      }

      // Case 2: Moving IN/OUT of Airlog Empty Holding with totes loaded
      if (
        isAirlogEmptyHoldingLocation(primaryLocation) &&
        hasTotesLoaded(trolleyStatus)
      ) {
        // Show error notification and return
        notification.error({
          message: "Action Not Allowed",
          description:
            "The Trolley isn't empty. Please unload all Totes before entering or exiting the Airlog Empty Holding Area.",
          duration: 5,
        });
        return;
      }
    }

    try {
      setIsUpdatingTrolleyLocation(true);
      const trolleyIdForSuccess = pendingTrolleyCode;
      await api.updateTrolleyLocation(
        pendingTrolleyCode,
        locationBarcode,
        locationType
      );

      // Track the action for secondary locations
      if (matchedLocation?.secondary_location) {
        const secondaryKey = `${matchedLocation.primary_location.toLowerCase()}|${matchedLocation.secondary_location.toLowerCase()}`;
        setTrolleySecondaryLocationActions((prev) => {
          const newMap = new Map(prev);
          newMap.set(secondaryKey, locationType);
          return newMap;
        });
      }

      notification.success({
        message: "Trolley Location Updated",
        description: `Trolley ${pendingTrolleyCode} updated to location (${locationType}).`,
      });
      handleTrolleyLocationCancel();
      refetchTrolleys();
      setTrolleyLocationSuccessId(trolleyIdForSuccess);
      setLocationSuccessMessage(
        <>
          Trolley {trolleyIdForSuccess}{" "}
          {locationType === "In"
            ? "is successfully assigned to"
            : "dispatched from"}{" "}
          <b>
            {matchedLocation?.primary_location || locationName || locationBarcode || "Location"}
          </b>
          <div
            style={{
              color: "#101010",
              fontWeight: 600,
              marginTop: 10,
            }}
          >
            ({locationType})
            {matchedLocation?.secondary_location
              ? `, ${matchedLocation.secondary_location}`
              : ""}
          </div>
        </>
      );

      setLocationSuccessOpen(true);
    } catch (error) {
      console.error("Failed to update trolley location", error);
      const errorDescription = axios.isAxiosError(error)
        ? error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message
        : error instanceof Error
        ? error.message
        : "We couldn't update the trolley location. Please try again.";

      // Check if error is related to invalid location
      const errorLower = errorDescription.toLowerCase();
      const isLocationError =
        errorLower.includes("location") &&
        (errorLower.includes("not found") ||
          errorLower.includes("invalid") ||
          errorLower.includes("not available") ||
          errorLower.includes("does not exist"));

      const locationDisplay =
        matchedLocation?.primary_location ||
        locationName ||
        locationBarcode ||
        "Location";

      if (isLocationError) {
        notification.info({
          message: "Location Not Found",
          description: `Scanned location "${locationDisplay}" is not available in the system`,
        });
      } else {
        notification.error({
          message: "Update Failed",
          description: errorDescription,
          duration: 5,
        });
      }

      // Close the modal on error and go back to location info page
      handleTrolleyLocationCancel();
    } finally {
      setIsUpdatingTrolleyLocation(false);
    }
  };

  // Cards are view-only in location info flow - no click handler
  const handleCardClick = () => {
    // Disabled - cards are view-only
  };

  const opItems = [
    {
      label: <div>Tote ({totes.length})</div>,
      value: "Tote",
      key: "1",
    },
    {
      label: <div>Trolley ({trolleys.length})</div>,
      value: "Trolley",
      key: "2",
    },
  ];

  return (
    <div className={Styles.mainContainer}>
      <div className={LocationStyles.contentContainer}>
        {/* Header with back button and title */}
        <div className={LocationStyles.header}>
          <Icon
            component={LeftIcon}
            onClick={() => navigate("/operator-landing")}
          />
          <h2 className={LocationStyles.title}>Location Info</h2>
        </div>

        {/* Scanned Location Section - Minimalist design */}
        {locationName && (
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                fontSize: "12px",
                color: "#878787",
                marginBottom: "4px",
                fontWeight: 500,
                fontFamily: "Open Sans, sans-serif",
              }}
            >
              Scanned Location
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#333",
                fontFamily: "Open Sans, sans-serif",
              }}
            >
              {locationName}
            </div>
          </div>
        )}

        <Row justify="center" className={Styles.spacingRow}>
          <Segmented
            options={opItems}
            className="mainSegments"
            value={segment}
            onChange={(value) => {
              setSegment(value);
            }}
          />
        </Row>

        {isLoading && displayedItems.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : (
          <div
            ref={scrollContainerRef}
            className={Styles.spacingRow}
            style={{
              overflowY: "auto",
              height: segment === "Trolley" ? "60vh" : "67vh",
            }}
          >
            {displayedItems.length === 0 ? (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#999" }}
              >
                {error
                  ? "Failed to load data"
                  : `No ${segment.toLowerCase()}s found at this location`}
              </div>
            ) : (
              <>
                {displayedItems.map((item, index) => (
                  <ListingCards
                    key={`${item.id || item.toteNo}-${index}`}
                    details={item}
                    listingType={segment}
                    onClick={undefined}
                  />
                ))}
                {isLoadingMore && (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Spin />
                  </div>
                )}
                {!hasMore && displayedItems.length > 0 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#999",
                      fontSize: "14px",
                    }}
                  >
                    No more items to load
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <UniversalScanButton
        mode="location-info"
        onToteScanned={handleToteScanned}
        onTrolleyScanned={handleTrolleyScanned}
        onLocationScanned={handleLocationScanned}
        isCheckingTote={isCheckingTote}
        isCheckingTrolley={isCheckingTrolley}
        scanErrorMessage={scanErrorMessage}
        onScanErrorClear={() => setScanErrorMessage(null)}
        availableActions={
          segment === "Tote"
            ? ["tote"]
            : segment === "Trolley"
            ? ["trolley"]
            : undefined
        }
      />

      <LocationConfirmationModal
        open={isToteLocationModalOpen}
        location={
          matchedLocation
            ? {
                primary: matchedLocation.primary_location,
                secondary: matchedLocation.secondary_location,
                barcode: matchedLocation.location_barcode,
                status: matchedLocation.current_location_type ?? null,
              }
            : locationBarcode
            ? {
                primary: locationBarcode,
                secondary: undefined,
                barcode: locationBarcode,
                status: null,
              }
            : null
        }
        lastScannedLocation={selectedToteData?.lastScannedLocation}
        lastLocationStatus={selectedToteData?.currentLocationType ?? null}
        onConfirm={handleToteLocationConfirm}
        onCancel={handleToteLocationCancel}
        isLoading={isUpdatingToteLocation}
        secondaryLocationActions={toteSecondaryLocationActions}
      />

      <TrolleyLocationConfirmationModal
        open={isTrolleyLocationModalOpen}
        location={
          matchedLocation
            ? {
                primary: matchedLocation.primary_location,
                secondary: matchedLocation.secondary_location,
                barcode: matchedLocation.location_barcode,
                status: matchedLocation.current_location_type ?? null,
              }
            : locationName
            ? {
                primary: locationName,
                secondary: undefined,
                barcode: locationBarcode || locationName,
                status: null,
              }
            : null
        }
        lastScannedLocation={undefined}
        lastLocationStatus={null}
        onConfirm={handleTrolleyLocationConfirm}
        onCancel={handleTrolleyLocationCancel}
        isLoading={isUpdatingTrolleyLocation}
        secondaryLocationActions={trolleySecondaryLocationActions}
      />

      <SuccessAlert
        open={toteLocationSuccessOpen}
        title={<b style={{ color: "#101010" }}>Tote Location Updated</b>}
        message={toteLocationSuccessMessage}
        buttonText="Done"
        onClose={() => {
          setToteLocationSuccessOpen(false);
          setToteLocationSuccessToteId(null);
          // Stay on Location Info page, just refetch data
          void refetchTotes();
        }}
      />

      <SuccessAlert
        open={locationSuccessOpen}
        title="Trolley Location Updated"
        message={locationSuccessMessage}
        buttonText="Done"
        onClose={() => {
          setLocationSuccessOpen(false);
          setTrolleyLocationSuccessId(null);
          // Stay on Location Info page, just refetch data
          void refetchTrolleys();
        }}
      />
    </div>
  );
};

export default LocationInfo;
